'use client';

import { useTranslations } from 'next-intl';
import type React from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { IconBack24 } from '@cineamo/ui/src/icons/ic-back';

import { Link, useRouter } from '@/i18n/routing';

/**
 * Takes the visitor back to wherever they came from (program, landing page, ...). The server HTML is a
 * plain link to the program, so crawlers and visitors who landed here directly still get a sensible
 * target; with a same-origin history entry the click steps back instead.
 */
export default function ProgramDetailBackButton({
    className,
    fallbackHref = '/program'
}: {
    className?: string;
    /** Where a visitor without in-site history lands; defaults to the plain program. */
    fallbackHref?: React.ComponentProps<typeof Link>['href'];
}): React.JSX.Element {
    const t = useTranslations();
    const router = useRouter();

    return (
        <Link
            href={fallbackHref}
            aria-label={t('program-detail.back')}
            // The negative margin keeps the icon where Figma puts it while the 36px backdrop shows it is a button
            className={cn(
                'flex shrink-0 items-center justify-center w-[36px] h-[36px] -m-8 rounded-8',
                'bg-[rgba(var(--on-background-color-rgb),0.1)] hover:bg-[rgba(var(--on-background-color-rgb),0.2)] transition-colors duration-150',
                className
            )}
            onClick={(event) => {
                // Client-side navigations leave document.referrer empty, so ask the Navigation API first
                const canGoBack = (globalThis as { navigation?: { canGoBack?: boolean } }).navigation?.canGoBack;
                const cameFromThisSite =
                    !!document.referrer && new URL(document.referrer).origin === globalThis.location.origin;
                if ((canGoBack ?? cameFromThisSite) && globalThis.history.length > 1) {
                    event.preventDefault();
                    router.back();
                }
            }}>
            <IconBack24 className="w-20 h-20" />
        </Link>
    );
}
